import NetxQueryClient, {
	type SuccessResponse,
	type QueryPager,
} from '@netx/core-next/lib/queryClient';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {
	getAttributeVocabularyValueDefaultFilters,
	getAttributeVocabularyValueDefaultSort,
	type AttributeVocabularyValueSort,
	type IAttributeVocabularyValue,
	type AttributeVocabularyValueFilter,
} from '../schema';

/**
 * `getAttributeVocabularyValues` request params
 * (what is used to send to server)
 */
export type GetAttributeVocabularyValuesRequestOptions = {
	/** attribute vocabulary id */
	id: number;
	filters?: Partial<AttributeVocabularyValueFilter>;
	sort?: Partial<AttributeVocabularyValueSort>;
	startIndex?: number;
	stopIndex?: number;
};

/**
 * `getAttributeVocabularyValues` request
 * (what gets sent to the server)
 */
export type GetAttributeVocabularyValuesRequest = [
	GetAttributeVocabularyValuesRequestOptions['id'],
	// right now we just support the single string filter search on vocab value
	string,
	AttributeVocabularyValueSort,
	QueryPager,
];

/**
 * `getAttributeVocabularyValues` response
 * (what comes back from the server)
 */
export type GetAttributeVocabularyValuesResponse =
	SuccessResponse<GetAttributeVocabularyValuesResult>;

/**
 * `getAttributeVocabularyValues` result
 * (what is utilized from the server response)
 */
export interface GetAttributeVocabularyValuesResult extends QueryPager {
	results: IAttributeVocabularyValue[];
}

export const getAttributeVocabularyValuesEndpoint =
	'getAttributeVocabularyValues';

/**
 * Vocabulary value auto list (enhanced)
 */
export const getAttributeVocabularyValues = async (
	{
		id,
		filters,
		sort,
		startIndex = 0,
		stopIndex = 25,
	}: GetAttributeVocabularyValuesRequestOptions,
	signal?: AbortSignal,
) => {
	const client = NetxQueryClient();

	const requestParams: GetAttributeVocabularyValuesRequest = [
		id,
		getAttributeVocabularyValueDefaultFilters({filters}).value ?? '',
		getAttributeVocabularyValueDefaultSort(sort),
		{startIndex, size: stopIndex - startIndex},
	];

	return client
		.post<GetAttributeVocabularyValuesResponse>(
			getAttributeVocabularyValuesEndpoint,
			requestParams,
			{signal},
		)
		.then((response) => response.data.result);
};

export default getAttributeVocabularyValues;
